export enum ApiErrorMessage {
  General = 'Something went wrong',
  Network = 'Network error',
  RequestAlreadyExists = 'Request already exists',
  UnableToSendRequest = 'Unable to send request',
  RequestCancelled = 'The api request was cancelled',
  BadRequest = 'The data entered is invalid',
}

export enum HttpMethod {
  Get = 'Get',
  Post = 'Post',
  Put = 'Put',
  Patch = 'Patch',
  Delete = 'Delete',
}

export enum ApiState {
  Idle = 'Idle',
  Pending = 'Pending',
  Success = 'Success',
  Failure = 'Failure',
}

export const ApiEndpoints = {
  Users: {
    Base: '/users',
    Profile: '/users/profile',
    Sync: '/users/sync',
    ClinicianRegistration: '/users/clinician-registration',
    ContactRequest: '/users/contact-request',
    RegistrationVerificationEmail: '/users/registration/verification-email',
    ResendVerification: '/users/resend-verification',
    UsernameSuggestions: '/users/username-suggestions',
    ForgotPassword: '/users/forgot-password',
    VerifyResetToken: '/users/verify-reset-token',
    ResetPassword: '/users/reset-password',
    ChangePassword: '/users/change-password',
    Offerings: '/users/offerings',
    PatientPin: '/users/patient-pin',
    BillingSetup: '/users/billing-setup',
    BillingSummary: '/users/billing-summary',
    BillingAutoConversion: '/users/billing-auto-conversion',
    Onboarding: '/users/onboarding',
  },
  Doctors: {
    Base: '/doctors',
    Bulk: '/doctors/bulk',
    BulkUpload: '/doctors/bulk/upload',
    ById: (id: string) => `/doctors/${id}`,
    Active: (id: string) => `/doctors/${id}/active`,
    Profile: (id: string) => `/doctors/${id}/profile`,
    SelfProfile: '/doctors/profile',
    InvitesVerify: '/doctors/invites/verify',
    InvitesAccept: '/doctors/invites/accept',
    InvitesResend: (id: string) => `/doctors/${id}/invites/resend`,
  },
  Sessions: {
    Base: '/sessions',
    Current: '/sessions/current',
    End: '/sessions/end',
    Abandon: '/sessions/abandon',
    AbandonReasons: '/sessions/abandon-reasons',
    Token: '/sessions/token',
    Analytics: '/sessions/analytics',
    AnalyticsDashboard: '/sessions/analytics/dashboard',
    AudioUploadUrl: '/sessions/audio-upload-url',
    Transcript: '/sessions/transcript',
    Patient: (username: string) => `/sessions/dr/${username}/patient`,
    Status: (username: string) => `/sessions/dr/${username}/status`,
    Pin: (username: string) => `/sessions/dr/${username}/pin`,
    Consults: (username: string) => `/sessions/dr/${username}/consults`,
  },
  Platform: {
    CustomerHistory: '/platform/customer-history',
    Countries: '/platform/countries',
    CountryById: (id: string) => `/platform/countries/${id}`,
    States: '/platform/states',
    StateById: (id: string) => `/platform/states/${id}`,
    Pms: '/platform/pms',
    PmsById: (id: string) => `/platform/pms/${id}`,
    Specialties: '/platform/specialties',
    SpecialtyById: (id: string) => `/platform/specialties/${id}`,
    SpecialtyLinkImpact: (id: string) =>
      `/platform/specialties/${id}/link-impact`,
    CppByNode: (nodeId: string) => `/platform/cpp/${nodeId}`,
    CppPublished: (nodeId: string) => `/platform/cpp/published/${nodeId}`,
    Cpp: '/platform/cpp',
    CppById: (id: string) => `/platform/cpp/${id}`,
    CppVersionsByNodePermanently: (nodeId: string) =>
      `/platform/cpp/node/${nodeId}`,
    CppStatusById: (id: string) => `/platform/cpp/${id}/status`,
    DriveConfig: '/platform/drive/config',
    DriveFolders: '/platform/drive/folders',
    DriveImport: '/platform/drive/import',
    Clinics: '/platform/clinics',
    ClinicById: (id: string) => `/platform/clinics/${id}`,
    ClinicActivate: (id: string) => `/platform/clinics/${id}/activate`,
    ClinicDeactivate: (id: string) => `/platform/clinics/${id}/deactivate`,
    ClinicUsers: (id: string) => `/platform/clinics/${id}/users`,
    ClinicUserById: (id: string, userId: string) =>
      `/platform/clinics/${id}/users/${userId}`,
    StripeProducts: '/platform/stripe/products',
    StripeProductById: (id: string) => `/platform/stripe/products/${id}`,
    StripeSync: '/platform/stripe/sync',
    BillingRateCard: '/platform/billing/rate-card',
    DoctorReconciliation: (id: string) =>
      `/platform/billing/doctors/${id}/reconciliation`,
    ClinicStripeCustomer: (id: string) =>
      `/platform/clinics/${id}/stripe-customer`,
    ClinicSubscription: (id: string) => `/platform/clinics/${id}/subscription`,
    ClinicInvoices: (id: string) => `/platform/clinics/${id}/invoices`,
    ClinicCheckoutSession: (id: string) =>
      `/platform/clinics/${id}/checkout-session`,
    ClinicCancelSubscription: (id: string) =>
      `/platform/clinics/${id}/cancel-subscription`,
    ClinicRefund: (id: string) => `/platform/clinics/${id}/refund`,
    PlatformUsers: '/platform/users',
    PlatformUserById: (id: string) => `/platform/users/${id}`,
    PlatformUserResendInvite: (id: string) =>
      `/platform/users/${id}/resend-invite`,
  },
  Billing: {
    Products: '/billing/products',
    CheckoutSession: '/billing/checkout-session',
    Subscription: '/billing/subscription',
    Invoices: '/billing/invoices',
    CustomerPortal: '/billing/customer-portal',
    Cancel: '/billing/cancel',
    Resubscribe: '/billing/resubscribe',
    ClinicProfile: '/billing/clinic-profile',
  },
  Reference: {
    Specialties: '/reference/specialties',
    FocusAreas: (id: string) => `/reference/subspecialties/${id}/focus-areas`,
    BLQuestions: (id: string) => `/reference/subspecialties/${id}/bl-questions`,
    States: '/reference/states',
    Regions: '/reference/regions',
    Pmses: '/reference/pmses',
    SecureMessagingProviders: '/reference/secure-messaging-providers',
  },
};

export const BaseUrl =
  import.meta.env.VITE_API_URL ??
  'Oops, Looks like you forgot to set the API URL in env';

export const NetworkErrorMessage = 'Network Error';

export const CONTENT_TYPE_HEADER_KEY = 'Content-Type';
export const BaseHeaders = {
  [CONTENT_TYPE_HEADER_KEY]: 'application/json',
  Accept: 'application/json',
  'ngrok-skip-browser-warning': '1',
};
export const AuthorizationHeaderKey = 'Authorization';
export const MULTIPART_FORM_DATA_CONTENT_TYPE = 'multipart/form-data';

export const AbortReason = 'No auth header value';
export const AbortErrorName = 'AbortError';
